import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { ToastContext } from '@weezevent/nacre';
import RateModel from '../../models/rateModel';
import RateGroup from '../../models/rateGroup';
import RateSelector from './rateSelector';
import EventSelector from './eventSelector';

/**
 * Render exception of resale
 *
 * @param eventList : raw object of list events gotten for call at pyvar api getAll
 * @param organizationId : organization Id
 * @param configs : Configs exceptions resale for one event
 * @param indiceException : number of current exception during the mapping
 * @param setExceptions : action to perform by parent component on change in current exception
 * @param deleteException : action to perform by parent component on delete the current exception
 * @param resaleMode : Mode of resale
 */
export default function ResaleException({ eventList, organizationId, configs, configExceptions, indiceException, setExceptions, deleteException, resaleMode }) {
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();

    // Containers of selected things
    let [event, setEvent] = React.useState(null);
    let [groups, setGroups] = React.useState([]);
    let [rates, setRates] = React.useState([]);
    let [exceptionMode, setExceptionMode] = React.useState('All');

    // Container of get Objects from model
    let [rateListRaw, setRateListRaw] = React.useState([]);
    let [groupsRateRaw, setGroupsRateRaw] = React.useState([]);

    // Container of mapped raw objects
    let [ratesAndGroups, setRatesAndGroups] = React.useState({ rates: {}, groups: {} });

    /**
     * Mapping of Rates and groups of rates to create Array containing the groups and rates like
     * {
     *      "rates" : [{ id: RateIdWithouGroup, name:"Rates Name without group"}, {...}],
     *      "groups": [{
     *          id: GroupId,
     *          name: "Group Name",
     *          rates:[{ id: RateIdContainingGroup, name:"Rates Name Containing Group"}, {...}]
     *      }]
     * }
     * Purpose : Make simple dict to create react Rate selector homogeneous with other repros
     */
    React.useEffect(() => {
        let groupsAndRates = groupsRateRaw.map(groups => {
            let rates = rateListRaw
                .filter(rate => rate.group === groups.id)
                .map(rate => {
                    return {
                        id: rate.id,
                        name: rate.name ? rate.name : rate.rate_template ? rate.rate_template.name : ''
                    };
                });
            return {
                id: groups.id,
                name: groups.name ? groups.name : groups.group_template ? groups.group_template.name : '',
                rates: rates
            };
        });
        let ratesMapped = rateListRaw
            .filter(rate => !rate.group)
            .map(rate => {
                return {
                    id: rate.id,
                    name: rate.name ? rate.name : rate.rate_template ? rate.rate_template.name : ''
                };
            });
        setRatesAndGroups({ rates: ratesMapped, groups: groupsAndRates });
    }, [rateListRaw, groupsRateRaw]);

    /**
     * Get rate list and group rates
     */
    React.useEffect(() => {
        if (event) {
            let rates = RateModel.getAll({ organizationId: organizationId, eventId: event });
            let groupsRate = RateGroup.getAll({ organizationId: organizationId, eventId: event });

            Promise.all([rates, groupsRate])
                .then(([rates, groupsRate]) => {
                    setRateListRaw(rates);
                    setGroupsRateRaw(groupsRate);
                })
                .catch(e => {
                    toast.error(t(`ticket.event.get_error`));
                });
        }
    }, [event, toast, organizationId, t]);

    /**
     * Action to perform on rate checked. It resolve :
     *      - if rate is in group, shall we put enabled true or false ?
     *      - if rate config does not have id, we can delete it but what shall we do if there is an id (and config already exist in pyvar)
     *      - add in check rates liste the good ID
     */
    const onRateChecked = React.useCallback(
        (rate, checked) => {
            let configToPush = configs;
            // Logical for configs on rate click
            let rate_obj = rateListRaw.find(rateObj => rateObj.id === rate.id);

            if (rate_obj.group) {
                let config_rate = configToPush.findIndex(conf => conf.event === rate_obj.event && conf.rate === rate_obj.id);
                let config_group = configToPush.find(conf => conf.event === rate_obj.event && conf.rate === null);
                // groupe is checked
                if (groups.includes(rate_obj.group)) {
                    if (config_rate === -1) {
                        // No configs on rate then we had it and enabled is different than config group
                        configToPush.push({ organization: organizationId, event: rate_obj.event, group: null, rate: rate_obj.id, enabled: !config_group.enabled });
                    } else {
                        // We had a config rate then we change only his enabled
                        configToPush[config_rate].enabled = !configToPush[config_rate].enabled;
                    }
                } else {
                    // Rate has a group but group is not checked
                    if (!checked) {
                        // If no Id and unchecked , then we can juste delete the config line
                        if (!configToPush[config_rate].id) {
                            configToPush[config_rate].deleted = true;
                        } else {
                            configToPush[config_rate].enabled = resaleMode;
                        }
                    } else {
                        // If check
                        if (config_rate === -1) {
                            configToPush.push({ organization: organizationId, event: rate_obj.event, group: null, rate: rate_obj.id, enabled: !resaleMode });
                        } else {
                            configToPush[config_rate].enabled = !resaleMode;
                        }
                    }
                }
            } else {
                let config_rate = configToPush.findIndex(conf => conf.event === rate_obj.event && conf.rate === rate_obj.id);
                if (config_rate !== -1) {
                    if (!checked && !configToPush[config_rate].id) {
                        // If no Id and unchecked , then we can just delete the config line
                        configToPush[config_rate].deleted = true;
                    } else {
                        // If check or unchecked but config line already exist in pyvar, we change it !
                        configToPush[config_rate].enabled = !configToPush[config_rate].enabled;
                    }
                } else {
                    configToPush.push({ organization: organizationId, event: rate_obj.event, group: null, rate: rate_obj.id, enabled: !resaleMode });
                }
            }
            // push new config
            setExceptions(indiceException, configToPush);

            if (checked) {
                //Select rate
                return setRates(rates => [...rates, rate.id]);
            }

            // unselect rate
            let oldRatesChecked = [...rates];
            let indiceRate = rates.findIndex(id => id === rate.id);
            oldRatesChecked.splice(indiceRate, 1);

            return setRates(oldRatesChecked);
        },
        [configs, rateListRaw, resaleMode, groups, organizationId, setExceptions, setRates, rates, indiceException]
    );

    /**
     * Action to perfom on group check.
     * On check group :
     *      - check all rates
     *      - create the exception for the group and delete (or pass enabled = !resaleMode) for rates in this group
     *
     * On uncheck group :
     *      - unckeck only the group ! not rates !
     *      - delete (or pass enabled = !resaleMode) the config of this group
     *      - create a config for each rate checked in group !
     *      - delete config for rates unchecked in group
     *
     */
    const onGroupChecked = React.useCallback(
        (group, checked) => {
            let configToPush = configs;
            let group_obj = groupsRateRaw.find(grpObj => grpObj.id === group.id);
            let config_group = configToPush.findIndex(conf => conf.event === group_obj.event && conf.rate === null && conf.group === group.id);
            if (!checked) {
                // this group was checked
                if (configToPush[config_group].id) {
                    configToPush[config_group].enabled = resaleMode;
                } else {
                    configToPush[config_group].deleted = true;
                }

                // check rates of groups does have config and put the same config as group
                group.rates.forEach(rate => {
                    let config_rate = configToPush.findIndex(conf => conf.event === group_obj.event && conf.rate === rate.id && conf.group === group.id);
                    if (config_rate === -1) {
                        // no config on rate ( so it is checked by group check)
                        configToPush.push({ organization: organizationId, event: group_obj.event, group: null, rate: rate.id, enabled: !resaleMode });
                    }

                    if (config_rate !== -1 && configToPush[config_rate].enabled === resaleMode && !configToPush[config_rate].id) {
                        configToPush[config_rate].deleted = true;
                    }
                });
            } else {
                if (config_group === -1) {
                    // Group does not have config
                    configToPush.push({ organization: organizationId, event: group_obj.event, group: group.id, rate: null, enabled: !resaleMode });
                } else {
                    configToPush[config_group].enabled = !resaleMode;
                }
                // check rates of groups does have config and put the same config as group
                group.rates.forEach(rate => {
                    let config_rate = configToPush.findIndex(conf => conf.event === group_obj.event && conf.rate === rate.id);
                    if (config_rate !== -1) {
                        // set the enabled status of group to rates
                        if (!configToPush[config_rate].id) {
                            configToPush[config_rate].deleted = true;
                        } else {
                            configToPush[config_rate].enabled = !resaleMode;
                        }
                    }
                });
            }

            // push new config
            setExceptions(indiceException, configToPush);

            if (checked) {
                let rates_group = group.rates;
                let ratesToAdd = rates_group
                    .filter(rate => !rates.includes(rate.id))
                    .map(rate => {
                        return rate.id;
                    });
                setRates(rates => [...rates, ...ratesToAdd]);
                return setGroups(groups => [...groups, group.id]);
            }

            let oldCategoriesChecked = [...groups];
            let indiceCategory = groups.findIndex(id => id === group.id);
            oldCategoriesChecked.splice(indiceCategory, 1);
            return setGroups(oldCategoriesChecked);
        },
        [configs, resaleMode, groupsRateRaw, groups, setExceptions, setRates, setGroups, rates, indiceException, organizationId]
    );

    /**
     * On load config
     */
    React.useEffect(() => {
        let tmpEvent = null;
        let tmpGroup = [];
        let tmpRates = [];
        let tmpExceptionMode = 'All';

        let configOnEvent = configs.filter(config => config.event && !config.group && !config.rate);
        let configOnGroups = configs.filter(config => config.event && config.group && !config.rate);
        let configOnRates = configs.filter(config => config.event && config.rate);

        if (configOnEvent.length) {
            if (configOnEvent[0].enabled === resaleMode) {
                tmpExceptionMode = 'Select';
            } else {
                tmpExceptionMode = 'All';
            }
        } else {
            if (configOnGroups.length || configOnRates.length) {
                tmpExceptionMode = 'Select';
            }
        }

        if (configs.length) {
            tmpEvent = configs[0].event;
        }

        // first config on groups :
        configOnGroups.forEach(config => {
            if (config.enabled !== resaleMode) {
                tmpGroup.push(config.group);
                let list_rates = rateListRaw.filter(rate => rate.group === config.group);
                list_rates.forEach(rate => {
                    tmpRates.push(rate.id);
                });
            }
        });

        // then config on rates :
        configOnRates.forEach(config => {
            if (config.enabled !== resaleMode) {
                tmpRates.push(config.rate);
            }

            if (config.enabled === resaleMode && tmpRates.includes(config.rate)) {
                let rate_index = tmpRates.findIndex(rate => rate === config.rate);
                let tmpConfig = tmpRates;
                tmpConfig.splice(rate_index, 1);
                tmpRates = tmpConfig;
            }
        });

        setEvent(tmpEvent);
        setGroups(tmpGroup);
        setRates(tmpRates);
        setExceptionMode(tmpExceptionMode);
    }, [configs, rateListRaw, resaleMode, setEvent, setGroups, setRates, setExceptionMode]);

    /**
     * On changing mode all rates or select rates .
     */
    const onChangeAllRates = React.useCallback(
        value => {
            if (value === 'All') {
                let tmpConfigs = [];

                configs.forEach(config => {
                    if (config.id) {
                        if (config.enabled === resaleMode) {
                            let tmpConf = { ...config };
                            tmpConf.enabled = !resaleMode;
                            tmpConfigs.push(tmpConf);
                        } else {
                            tmpConfigs.push(config);
                        }
                    }
                });

                // push config for all event
                tmpConfigs.push({ organization: organizationId, event: event, group: null, rate: null, enabled: !resaleMode });
                setExceptions(indiceException, tmpConfigs);
            }

            if (value === 'Select') {
                let tmpConfigs = configs;
                let eventConfigIndex = tmpConfigs.findIndex(config => config.event && !config.group && !config.rate);

                if (eventConfigIndex !== -1) {
                    tmpConfigs[eventConfigIndex].enabled = resaleMode;
                }
                setExceptions(indiceException, tmpConfigs);
            }

            // Set exception mode
            setExceptionMode(value);
        },
        [configs, organizationId, resaleMode, setExceptions, setExceptionMode, indiceException, event]
    );

    const onChangeEvent = React.useCallback(
        value => {
            if (!configs.length) {
                // No config, than we must creat one !
                let tmpConfig = [];
                tmpConfig.push({ organization: organizationId, event: value, rate: null, group: null, enabled: !resaleMode });
                setExceptions(indiceException, tmpConfig);
            } else {
                // some configs are already here so we will canceled them
                let tmpConfig = configs;
                tmpConfig.forEach(config => {
                    if (config.id) {
                        config.enabled = resaleMode;
                    } else {
                        let conf_index = tmpConfig.findIndex(conf => conf === config);
                        tmpConfig.splice(conf_index, 1);
                    }
                });
                tmpConfig.push({ organization: organizationId, event: value, rate: null, group: null, enabled: !resaleMode });
                setExceptions(indiceException, tmpConfig);
            }

            setEvent(value);
        },
        [configs, organizationId, resaleMode, setExceptions, setEvent, indiceException]
    );

    return (
        <div className="ruleblockitem">
            <div className="ruleblockitem-index">{indiceException + 1}</div>
            <div className="ruleblockitem-body">
                <EventSelector resaleMode={resaleMode} configExceptions={configExceptions} eventList={eventList} onSelect={onChangeEvent} eventSelected={event} />
                <RateSelector
                    event={event}
                    organizationId={organizationId}
                    onSelectException={onChangeAllRates}
                    mode={exceptionMode}
                    ratesAndGroups={ratesAndGroups}
                    onRateChecked={onRateChecked}
                    ratesChecked={rates}
                    onGroupChecked={onGroupChecked}
                    groups={groups}
                />
            </div>
            <div
                className="ruleblockitem-icon"
                onClick={() => {
                    deleteException(indiceException);
                }}
            >
                <Icon name={'close'} />
            </div>
        </div>
    );
}

ResaleException.propTypes = {
    eventList: PropTypes.array.isRequired,
    organizationId: PropTypes.string.isRequired,
    configs: PropTypes.array.isRequired,
    configExceptions: PropTypes.array.isRequired,
    indiceException: PropTypes.number.isRequired,
    setExceptions: PropTypes.func.isRequired,
    deleteException: PropTypes.func.isRequired,
    resaleMode: PropTypes.bool.isRequired
};
